import { useCallback, useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { WeatherForecastPointSelectionState } from '@ebalance/proto/bfftypes/v1/weather_forecast_point_selection_state_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Box,
  Flex,
  Link,
  Spacing,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowUpRightFromSquareRegular,
  FaGearRegular,
} from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { iconSize } from '~/constants/iconSize'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { WeatherForecastPointCards } from '~/pages/AdminWeatherForecastPoints/components/WeatherForecastPointCards'
import { useAreaPointSave } from '~/pages/AdminWeatherForecastPoints/hooks/useAreaPointSave'
import { useListWeatherForecastPoints } from '~/pages/AdminWeatherForecastPoints/hooks/useListWeatherForecastPoints'
import { convertListResponse } from '~/pages/AdminWeatherForecastPoints/models/converter'
import type { WeatherForecastPointRow } from '~/pages/AdminWeatherForecastPoints/models/types'
import { routePaths } from '~/routes/routePaths'

/**
 * エリア別 気象予測地点設定ページ (/admin/weather-forecast-points)。
 *
 * design: `design/admin/weather-forecast-points.html` (カードグリッド型)
 *
 * AC-WFP-002: 「未選択」プレースホルダーは提供しない (issue #5191)
 * AC-WFP-007: 表示する地点は backend が解決した実効地点をそのまま使う。 未設定・
 *   候補外のフォールバックは backend 1 箇所 (`valueobject.ResolveWeatherForecastPoint`)
 *   にあり、 画面はここで再解決しない。 初期選択は表示のみで、
 *   未変更エリアはサーバー保存対象にならない
 * AC-WFP-008: 保存済み地点が候補から外れた (失効) エリアは info アラートで明示し、
 *   「代表地点で確定する」で保存できるようにする (同値のままでは onChange が発火せず
 *    利用者操作で修復できないため)
 * AC-WFP-003/004: 地点を選んだ時にそのエリアだけを保存する (保存ボタン・未保存の表示・
 *   離脱確認・成功トーストは持たない。 conventions.md §値の保存のしかた)。
 *   前の保存が終わる前に同じエリアを選び直した時は、 最後の値だけを前の保存の応答の
 *   updatedAt で順に送る (自分の前の保存を競合として扱わない)
 * AC-WFP-005: 楽観ロック競合時 エラートースト + 最新値の再取得 (入力は残さない)
 * AC-WFP-006: ネットワーク断/サーバーエラー時 失敗したエリアだけ保存済みの地点
 *   (無ければ代表地点 = row.locationName) へ戻し、 エラートースト
 * AC-WFP-008: 「代表地点で確定する」は失効エリアそれぞれを、 選択と同じ保存経路で保存する
 * 閲覧者ロール: select 無効化
 *   (ロール別の編集可否は本 spec のスコープ外 (§スコープ外「地点設定の細分化権限」) で
 *    対応する AC は無い。 組織内ロール判定もフロントエンド未実装で、 BFF の認可ガードが
 *    主防御となるため、 今回は false 固定で実装する)
 */

// TODO: 組織ロール hook 実装後に使用するロールを判定する (spec スコープ外のため AC 無し)
const IS_VIEWER_TODO = false

const JMA_URL = 'https://www.jma.go.jp/'

export const AdminWeatherForecastPointsPage = (): React.ReactElement => {
  const { t } = useTranslation()

  const { data: listResponse } = useListWeatherForecastPoints()
  const { pendingMap, saveArea } = useAreaPointSave()

  const rows = useMemo<WeatherForecastPointRow[]>(() => {
    if (listResponse === undefined) return []
    const base = convertListResponse(
      listResponse.points,
      listResponse.candidates,
    )
    return base.map((row) => ({
      ...row,
      pendingLocationName: pendingMap.get(row.areaCode),
    }))
  }, [listResponse, pendingMap])

  // AC-WFP-001: 業務エリア外のエリアは行自体を表示しない (表示フィルタのみ。
  // 保存は rows の全エリアが対象になりうる)。取得失敗時は全エリア表示に degrade する。
  const { selectableAreaCodes } = useBusinessAreaFilter(AREA_CODE_OPTIONS)
  const visibleRows = useMemo(
    () => rows.filter((row) => selectableAreaCodes.includes(row.areaCode)),
    [rows, selectableAreaCodes],
  )

  /**
   * AC-WFP-008: 明示選択が地点マスタ改廃で失効し、 代表地点に切り替わったエリア。
   * 変更前は需要想定側が「気象データなし」に落ちる可視の異常だったが、 代表地点で
   * 正常表示されるようになった分、 切り替わった事実をここで明示しないと利用者から
   * 見て無症状になる。
   */
  const revokedRows = useMemo(
    () =>
      visibleRows.filter(
        (row) =>
          row.selectionState ===
          WeatherForecastPointSelectionState.DEFAULT_REVOKED,
      ),
    [visibleRows],
  )
  const revokedAreas = revokedRows
    .map((row) => `${convertAreaLabel(row.areaCode)} (${row.locationName})`)
    .join('、')

  const handleSelectChange = useCallback(
    (areaCode: AreaCode, locationName: string) => {
      const row = rows.find((r) => r.areaCode === areaCode)
      if (row === undefined) return
      saveArea(row, locationName)
    },
    [rows, saveArea],
  )

  /**
   * AC-WFP-008: 失効エリアを代表地点のまま確定する。 画面は既に代表地点を表示して
   * いるので select の同値選択では onChange が発火せず、 利用者操作だけでは DB の
   * 失効値を直せない。 失効エリアそれぞれを、 選択と同じエリアごとの保存で保存する。
   */
  const handleConfirmRevoked = useCallback(() => {
    for (const row of revokedRows) {
      saveArea(row, row.locationName)
    }
  }, [revokedRows, saveArea])

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:adminWeatherForecastPoints.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        {
          label: t('common:route.adminWeatherForecastPoints'),
          bold: true,
        },
      ]}
      headerExtra={
        <Text color="Text.midEmphasis" fontSize="md">
          {t('page:adminWeatherForecastPoints.source.prefix')}
          <Link href={JMA_URL} rel="noopener noreferrer" target="_blank">
            {t('page:adminWeatherForecastPoints.source.linkLabel')}
            <FaArrowUpRightFromSquareRegular
              boxSize={iconSize.externalLink}
              aria-hidden="true"
            />
          </Link>
          {t('page:adminWeatherForecastPoints.source.urlNote')}
        </Text>
      }
    >
      <PageBody>
        <PageScrollArea>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: Spacing[4].value,
            }}
          >
            {/* page description (design `.page-description`: 需要想定画面への内部リンクを含む) */}
            <PageIntro
              description={
                <>
                  {t('page:adminWeatherForecastPoints.description.line1Before')}
                  <Link asChild>
                    {/* design は当日画面を指すが daily は caseId 必須で直接遷移できないため一覧へ */}
                    <RouterLink to={routePaths.demandForecastList.path}>
                      {t(
                        'page:adminWeatherForecastPoints.description.demandLink',
                      )}
                    </RouterLink>
                  </Link>
                  {t('page:adminWeatherForecastPoints.description.line1After')}
                  <br />
                  {t('page:adminWeatherForecastPoints.description.line2')}
                </>
              }
            />

            {/* design `.st-card`: カードタイトル行 + アラート群 + カードグリッド */}
            <Box
              padding={4}
              background="Surface.primary"
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="lg"
              boxShadow="sm"
            >
              <Flex alignItems="center" gap={2} marginBottom={2}>
                {/* カード見出しは 16px (role sectionTitle = lg/semibold)。
                    label (14px) では下の項目ラベルと同格で見出しに読めなかった
                    (2026-09-01 デザイナー指摘「見出しだけ 16px に」)。 */}
                <Text textStyle="sectionTitle" color="Text.highEmphasis">
                  {t('page:adminWeatherForecastPoints.cardTitle')}
                </Text>
                <TertiaryButton marginLeft="auto" asChild>
                  <RouterLink to={routePaths.settings.path}>
                    <FaGearRegular
                      boxSize={iconSize.labeledButton}
                      aria-hidden="true"
                    />
                    {t('page:adminWeatherForecastPoints.action.settingsLink')}
                  </RouterLink>
                </TertiaryButton>
              </Flex>

              <Flex flexDirection="column" gap={3}>
                {/* 閲覧者ロール (spec スコープ外・AC なし)。閲覧者には編集の権限が無く、
                    「代表地点で確定する」も保存なので押せない。権限の無い人に導線を出す
                    矛盾を避け、閲覧のみの 1 本に失効の情報を本文でまとめる
                    (alert-placement §3)。導線は出さない。 */}
                {IS_VIEWER_TODO ? (
                  /* 今の役割の注記 (操作を求めない) なので Alert でなく本文 (alert-placement.md §0) */
                  <Text textStyle="body" color="Text.highEmphasis">
                    <Text as="span" fontWeight="semibold">
                      {t('page:adminWeatherForecastPoints.viewer.note')}
                    </Text>
                    {revokedRows.length > 0 && (
                      <>
                        {' '}
                        {t(
                          'page:adminWeatherForecastPoints.viewer.revokedDesc',
                          {
                            areas: revokedAreas,
                          },
                        )}
                      </>
                    )}
                  </Text>
                ) : (
                  /* AC-WFP-008: 明示選択が失効したエリアの明示 + 確定導線 */
                  revokedRows.length > 0 && (
                    <CompactAlert
                      actions={
                        <TertiaryButton
                          onClick={handleConfirmRevoked}
                          size="sm"
                        >
                          {t('page:adminWeatherForecastPoints.revoked.confirm')}
                        </TertiaryButton>
                      }
                      description={t(
                        'page:adminWeatherForecastPoints.revoked.desc',
                        { areas: revokedAreas },
                      )}
                      status="warning"
                      title={t('page:adminWeatherForecastPoints.revoked.title')}
                    />
                  )
                )}

                <WeatherForecastPointCards
                  isViewer={IS_VIEWER_TODO}
                  onSelectChange={handleSelectChange}
                  rows={visibleRows}
                />
              </Flex>
            </Box>
          </Box>
        </PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
